import React, { useState, useEffect } from 'react';
import {
  FaTicketAlt as Ticket,
  FaSearch as Search,
  FaGift as Gift,
  FaTags as Tags,
  FaPercent as Percent,
  FaDollarSign as Dollar,
  FaClock as Clock,
  FaUsers as Users,
  FaCopy as Copy,
  FaCheck as Check,
  FaExclamationTriangle as Warning,
  FaInfoCircle as Info,
  FaStar as Star,
  FaFire as Fire,
  FaCalendarAlt as Calendar,
  FaFilter as Filter
} from 'react-icons/fa';
import {
  Coupon,
  CouponCategory,
  CouponType,
  CouponValidationResult,
  UserRole
} from '../../types/coupons';
import { useAuth } from '../../hooks/useAuth';

interface UserCouponsPageProps {}

const UserCouponsPage: React.FC<UserCouponsPageProps> = () => {
  const { user } = useAuth();
  const [availableCoupons, setAvailableCoupons] = useState<Coupon[]>([]);
  const [myCoupons, setMyCoupons] = useState<Coupon[]>([]);
  const [activeTab, setActiveTab] = useState<'available' | 'my-coupons' | 'redeem'>('available');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<CouponCategory | 'all'>('all');
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemResult, setRedeemResult] = useState<CouponValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState('');

  // تحميل الكوبونات المتاحة
  useEffect(() => {
    const mockCoupons: Coupon[] = [
      {
        id: '1',
        code: 'WELCOME2024',
        title: 'خصم ترحيبي للطلاب الجدد',
        description: 'احصل على خصم 20% على أول عملية شراء. ساري المفعول حتى نهاية العام.',
        type: 'percentage',
        value: 20,
        category: 'welcome',
        status: 'active',
        usageLimit: 100,
        usageCount: 45,
        validFrom: '2024-01-01T00:00:00Z',
        validUntil: '2024-12-31T23:59:59Z',
        createdAt: new Date().toISOString(),
        createdBy: 'admin',
        updatedAt: new Date().toISOString(),
        conditions: {
          minAmount: 100,
          applicableRoles: ['student', 'parent'],
          applicableServices: ['tuition', 'materials'],
          firstTimeOnly: true,
          userUsageLimit: 1
        },
        metadata: {
          campaign: 'new_student_campaign',
          tags: ['new_users', 'students'],
          priority: 1,
          stackable: false,
          autoApply: false,
          analytics: {
            impressions: 500,
            clicks: 120,
            uses: 45,
            conversions: 45,
            revenue: 4500,
            savings: 1125
          }
        }
      },
      {
        id: '2',
        code: 'RAMADAN50',
        title: 'خصم رمضان الكريم',
        description: 'خصم خاص بمناسبة شهر رمضان المبارك - 50 جنيهًا على الدورات',
        type: 'fixed_amount',
        value: 50,
        category: 'seasonal',
        status: 'active',
        usageLimit: 200,
        usageCount: 89,
        validFrom: '2024-03-01T00:00:00Z',
        validUntil: '2024-04-30T23:59:59Z',
        createdAt: new Date(Date.now() - 2592000000).toISOString(),
        createdBy: 'admin',
        updatedAt: new Date().toISOString(),
        conditions: {
          minAmount: 200,
          applicableRoles: ['student', 'parent'],
          applicableServices: ['tuition', 'exam_prep'],
          userUsageLimit: 1
        },
        metadata: {
          campaign: 'ramadan_2024',
          tags: ['ramadan', 'seasonal', 'limited_time'],
          priority: 2,
          stackable: false,
          analytics: {
            impressions: 800,
            clicks: 200,
            uses: 89,
            conversions: 89,
            revenue: 8900,
            savings: 4450
          }
        }
      },
      {
        id: '3',
        code: 'LOYALTY15',
        title: 'مكافأة العملاء المخلصين',
        description: 'شكراً لولائك! احصل على خصم 15% على جميع الخدمات',
        type: 'percentage',
        value: 15,
        category: 'loyalty',
        status: 'active',
        usageLimit: -1,
        usageCount: 156,
        validFrom: '2024-01-01T00:00:00Z',
        validUntil: '2024-12-31T23:59:59Z',
        createdAt: new Date(Date.now() - 5184000000).toISOString(),
        createdBy: 'admin',
        updatedAt: new Date().toISOString(),
        conditions: {
          minAmount: 150,
          applicableRoles: ['student', 'parent'],
          applicableServices: ['tuition', 'materials', 'exam_prep', 'consultation'],
          userUsageLimit: 3
        },
        metadata: {
          campaign: 'loyalty_program',
          tags: ['loyalty', 'repeat_customers'],
          priority: 3,
          stackable: true,
          analytics: {
            impressions: 1200,
            clicks: 320,
            uses: 156,
            conversions: 156,
            revenue: 18720,
            savings: 2808
          }
        }
      },
      {
        id: '4',
        code: 'FLASH30',
        title: 'عرض البرق - خصم 30%',
        description: '⚡ عرض سريع! خصم 30% لمدة محدودة على جميع الدورات',
        type: 'percentage',
        value: 30,
        category: 'flash_sale',
        status: 'active',
        usageLimit: 50,
        usageCount: 32,
        validFrom: new Date().toISOString(),
        validUntil: new Date(Date.now() + 86400000 * 3).toISOString(), // 3 أيام
        createdAt: new Date().toISOString(),
        createdBy: 'admin',
        updatedAt: new Date().toISOString(),
        conditions: {
          minAmount: 300,
          applicableRoles: ['student', 'parent'],
          applicableServices: ['tuition'],
          userUsageLimit: 1
        },
        metadata: {
          campaign: 'flash_sale_march',
          tags: ['flash_sale', 'limited_time', 'high_discount'],
          priority: 1,
          stackable: false,
          analytics: {
            impressions: 250,
            clicks: 80,
            uses: 32,
            conversions: 32,
            revenue: 4800,
            savings: 2057
          }
        }
      }
    ];

    // كوبونات المستخدم (المستخدمة)
    const mockMyCoupons: Coupon[] = [
      {
        ...mockCoupons[0],
        id: 'used_1',
        metadata: {
          ...mockCoupons[0].metadata,
          usedAt: new Date(Date.now() - 1296000000).toISOString(), // منذ 15 يوم
          orderAmount: 250,
          discountApplied: 50
        }
      }
    ];

    setAvailableCoupons(mockCoupons);
    setMyCoupons(mockMyCoupons);
    setIsLoading(false);
  }, []);

  // تصفية الكوبونات المتاحة
  const filteredCoupons = availableCoupons.filter(coupon => {
    // البحث
    const matchesSearch = !searchQuery || 
      coupon.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coupon.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coupon.code.toLowerCase().includes(searchQuery.toLowerCase());

    // الفئة
    const matchesCategory = filterCategory === 'all' || coupon.category === filterCategory;

    // التحقق من صلاحية المستخدم
    const userRole = user?.role as UserRole;
    const isApplicableForUser = coupon.conditions.applicableRoles.includes(userRole);

    return matchesSearch && matchesCategory && isApplicableForUser && coupon.status === 'active';
  });

  // نسخ كود الكوبون
  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 2000);
    });
  };

  // التحقق من صحة الكوبون
  const validateCoupon = async (code: string) => {
    setIsValidating(true);
    
    // محاكاة استدعاء API
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const coupon = availableCoupons.find(c => c.code === code);
    
    if (!coupon) {
      setRedeemResult({
        isValid: false,
        errors: ['الكوبون غير موجود أو غير صالح']
      });
    } else if (coupon.status !== 'active') {
      setRedeemResult({
        isValid: false,
        errors: ['هذا الكوبون غير نشط حالياً']
      });
    } else if (new Date(coupon.validUntil) < new Date()) {
      setRedeemResult({
        isValid: false,
        errors: ['انتهت صلاحية هذا الكوبون']
      });
    } else {
      const discountAmount = coupon.type === 'percentage' 
        ? (300 * coupon.value / 100) // مثال على مبلغ 300 جنيه
        : coupon.value;
      
      setRedeemResult({
        isValid: true,
        coupon,
        discountAmount,
        finalAmount: 300 - discountAmount,
        warnings: coupon.conditions.firstTimeOnly ? ['هذا الكوبون للمستخدمين الجدد فقط'] : undefined
      });
    }
    
    setIsValidating(false);
  };

  // الحصول على أيقونة النوع
  const getTypeIcon = (type: CouponType) => {
    switch (type) {
      case 'percentage': return <span className="w-5 h-5 text-center">%</span>;
      case 'fixed_amount': return <span className="w-5 h-5 text-center">$</span>;
      default: return <span className="w-5 h-5 text-center">🎁</span>;
    }
  };

  // الحصول على لون الفئة
  const getCategoryColor = (category: CouponCategory) => {
    switch (category) {
      case 'welcome': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'loyalty': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'seasonal': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'flash_sale': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'referral': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  // الحصول على نص الفئة
  const getCategoryText = (category: CouponCategory) => {
    switch (category) {
      case 'welcome': return 'ترحيبي';
      case 'loyalty': return 'ولاء';
      case 'seasonal': return 'موسمي';
      case 'flash_sale': return 'عرض البرق';
      case 'referral': return 'إحالة';
      case 'bulk_discount': return 'خصم كمية';
      default: return category;
    }
  };

  // تنسيق التاريخ
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // تنسيق العملة
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'EGP'
    }).format(amount);
  };

  // حساب الأيام المتبقية
  const getDaysRemaining = (validUntil: string) => {
    const now = new Date();
    const expiry = new Date(validUntil);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* رأس الصفحة */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center justify-center gap-3">
            <span className="w-8 h-8 text-blue-600" >🏷️</span>
            كوبونات الخصم والعروض
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            اكتشف أفضل العروض والخصومات المتاحة لك
          </p>
        </div>

        {/* علامات التبويب */}
        <div className="flex justify-center space-x-8 border-b border-gray-200 dark:border-gray-700 mb-6">
          <button
            onClick={() => setActiveTab('available')}
            className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'available'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            الكوبونات المتاحة
          </button>
          <button
            onClick={() => setActiveTab('my-coupons')}
            className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'my-coupons'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            كوبوناتي
          </button>
          <button
            onClick={() => setActiveTab('redeem')}
            className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'redeem'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            استخدام كوبون
          </button>
        </div>

        {/* الكوبونات المتاحة */}
        {activeTab === 'available' && (
          <div className="space-y-6">
            {/* شريط البحث والفلترة */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <span className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" >🏷️</span>
                    <input
                      type="text"
                      placeholder="البحث في الكوبونات..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pr-10 pl-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 text-gray-400" >🏷️</span>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value as CouponCategory | 'all')}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    aria-label="فلترة حسب الفئة"
                  >
                    <option value="all">جميع الفئات</option>
                    <option value="welcome">ترحيبي</option>
                    <option value="loyalty">ولاء</option>
                    <option value="seasonal">موسمي</option>
                    <option value="flash_sale">عرض البرق</option>
                    <option value="referral">إحالة</option>
                  </select>
                </div>
              </div>
            </div>

            {/* شبكة الكوبونات */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 animate-pulse">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                    <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                  </div>
                ))
              ) : filteredCoupons.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <span className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" >🏷️</span>
                  <p className="text-gray-500 dark:text-gray-400 text-lg">لا توجد كوبونات متاحة حالياً</p>
                </div>
              ) : (
                filteredCoupons.map((coupon) => {
                  const daysRemaining = getDaysRemaining(coupon.validUntil);
                  const isExpiringSoon = daysRemaining <= 7;
                  
                  return (
                    <div key={coupon.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow">
                      {/* رأس الكوبون */}
                      <div className={`p-4 ${
                        coupon.category === 'flash_sale' 
                          ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
                          : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {getTypeIcon(coupon.type)}
                            <span className="font-bold text-lg">
                              {coupon.type === 'percentage' 
                                ? `${coupon.value}%` 
                                : formatCurrency(coupon.value)
                              }
                            </span>
                          </div>
                          {coupon.category === 'flash_sale' && <span className="w-5 h-5" >🏷️</span>}
                        </div>
                        <h3 className="font-semibold text-lg">{coupon.title}</h3>
                      </div>

                      {/* محتوى الكوبون */}
                      <div className="p-4 space-y-4">
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          {coupon.description}
                        </p>

                        {/* معلومات إضافية */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 dark:text-gray-400">الحد الأدنى:</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatCurrency(coupon.conditions.minAmount || 0)}
                            </span>
                          </div>
                          
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 dark:text-gray-400">صالح حتى:</span>
                            <span className={`font-medium ${isExpiringSoon ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
                              {formatDate(coupon.validUntil)}
                            </span>
                          </div>

                          {isExpiringSoon && (
                            <div className="flex items-center gap-2 text-red-600 text-sm">
                              <span className="w-4 h-4" >🏷️</span>
                              <span>ينتهي خلال {daysRemaining} أيام</span>
                            </div>
                          )}
                        </div>

                        {/* شارات الفئة */}
                        <div className="flex items-center justify-between">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(coupon.category)}`}>
                            {getCategoryText(coupon.category)}
                          </span>
                          
                          {coupon.usageLimit > 0 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              متبقي: {coupon.usageLimit - coupon.usageCount}
                            </div>
                          )}
                        </div>

                        {/* كود الكوبون */}
                        <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-500 dark:text-gray-400">الكود:</span>
                              <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-sm font-mono">
                                {coupon.code}
                              </code>
                            </div>
                            <button
                              onClick={() => copyCode(coupon.code)}
                              className="flex items-center gap-1 px-3 py-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm transition-colors"
                            >
                              {copiedCode === coupon.code ? (
                                <>
                                  <span className="w-4 h-4" >🏷️</span>
                                  تم النسخ
                                </>
                              ) : (
                                <>
                                  <span className="w-4 h-4" >🏷️</span>
                                  نسخ
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* كوبوناتي */}
        {activeTab === 'my-coupons' && (
          <div className="space-y-6">
            {myCoupons.length === 0 ? (
              <div className="text-center py-12">
                <span className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" >🏷️</span>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  لم تستخدم أي كوبونات بعد
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  ابدأ بتصفح الكوبونات المتاحة واستفد من العروض الرائعة
                </p>
                <button
                  onClick={() => setActiveTab('available')}
                  className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  تصفح الكوبونات
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {myCoupons.map((coupon) => (
                  <div key={coupon.id} className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{coupon.title}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          الكود: {coupon.code}
                        </p>
                      </div>
                      <div className="text-left">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          تم الاستخدام: {formatDate(coupon.metadata?.usedAt || '')}
                        </div>
                        <div className="text-sm font-medium text-green-600">
                          وفرت: {formatCurrency(coupon.metadata?.discountApplied || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* استخدام كوبون */}
        {activeTab === 'redeem' && (
          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">
                استخدام كوبون خصم
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    كود الكوبون
                  </label>
                  <input
                    type="text"
                    value={redeemCode}
                    onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                    placeholder="أدخل كود الكوبون"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center font-mono text-lg"
                  />
                </div>

                <button
                  onClick={() => validateCoupon(redeemCode)}
                  disabled={!redeemCode.trim() || isValidating}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isValidating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      جاري التحقق...
                    </>
                  ) : (
                    <>
                      <span className="w-4 h-4" >🏷️</span>
                      التحقق من الكوبون
                    </>
                  )}
                </button>

                {/* نتيجة التحقق */}
                {redeemResult && (
                  <div className={`p-4 rounded-lg border ${
                    redeemResult.isValid 
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                  }`}>
                    {redeemResult.isValid ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                          <span className="w-5 h-5" >🏷️</span>
                          <span className="font-medium">الكوبون صالح!</span>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>الخصم:</span>
                            <span className="font-medium text-green-600">
                              {formatCurrency(redeemResult.discountAmount || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>المبلغ النهائي:</span>
                            <span className="font-medium">
                              {formatCurrency(redeemResult.finalAmount || 0)}
                            </span>
                          </div>
                        </div>

                        {redeemResult.warnings && redeemResult.warnings.length > 0 && (
                          <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded">
                            {redeemResult.warnings.map((warning, index) => (
                              <div key={index} className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200 text-sm">
                                <span className="w-4 h-4" >🏷️</span>
                                <span>{warning}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                          <span className="w-5 h-5" >🏷️</span>
                          <span className="font-medium">الكوبون غير صالح</span>
                        </div>
                        
                        {redeemResult.errors?.map((error, index) => (
                          <p key={index} className="text-sm text-red-700 dark:text-red-300">
                            {error}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserCouponsPage;

